import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, Power, PowerOff, Clock, ClipboardList, ListChecks, ChevronDown, ChevronUp, X, GripVertical, Merge, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const ServicesPage = ({ user, onLogout }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: 'time_based' });
  const [expandedService, setExpandedService] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [procedureSteps, setProcedureSteps] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newStepDescription, setNewStepDescription] = useState('');
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeStep, setMergeStep] = useState(1);
  const [mergePrimary, setMergePrimary] = useState(null);
  const [mergeSecondary, setMergeSecondary] = useState(null);
  const [mergeChecklistFrom, setMergeChecklistFrom] = useState('primary');
  const [mergeStepsFrom, setMergeStepsFrom] = useState('primary');
  const [merging, setMerging] = useState(false);
  const [migratingUppercase, setMigratingUppercase] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Service name is required');
      return;
    }

    try {
      await api.post('/services', formData);
      toast.success('Service created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', type: 'time_based' });
      fetchServices();
    } catch (error) {
      toast.error('Failed to create service');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Service name is required');
      return;
    }

    try {
      await api.put(`/services/${editingService.id}`, { name: formData.name });
      toast.success('Service updated successfully');
      setEditingService(null);
      setFormData({ name: '', type: 'time_based' });
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const handleToggleStatus = async (service) => {
    try {
      const newStatus = service.status === 'active' ? 'inactive' : 'active';
      await api.put(`/services/${service.id}`, { status: newStatus });
      toast.success(`Service ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service status');
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Are you sure you want to delete "${service.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/services/${service.id}`);
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({ name: service.name, type: service.type });
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setEditingService(null);
    setFormData({ name: '', type: 'time_based' });
  };

  // Checklist Management
  const openChecklistModal = (service) => {
    setShowChecklistModal(true);
    setChecklistItems(service.pre_procedure_checklist || []);
    setEditingService(service);
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setChecklistItems([...checklistItems, { id: `temp-${Date.now()}`, description: newChecklistItem.trim() }]);
    setNewChecklistItem('');
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const saveChecklist = async () => {
    try {
      await api.put(`/services/${editingService.id}`, {
        pre_procedure_checklist: checklistItems.map(item => ({ description: item.description }))
      });
      toast.success('Checklist saved successfully');
      setShowChecklistModal(false);
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error('Failed to save checklist');
    }
  };

  // Procedure Steps Management
  const openStepsModal = (service) => {
    setShowStepsModal(true);
    setProcedureSteps(service.procedure_steps || []);
    setEditingService(service);
  };

  const addProcedureStep = () => {
    if (!newStepDescription.trim()) return;
    setProcedureSteps([...procedureSteps, { 
      id: `temp-${Date.now()}`, 
      description: newStepDescription.trim(),
      order: procedureSteps.length + 1
    }]);
    setNewStepDescription('');
  };

  const removeProcedureStep = (index) => {
    const updated = procedureSteps.filter((_, i) => i !== index);
    // Re-order remaining steps
    setProcedureSteps(updated.map((step, i) => ({ ...step, order: i + 1 })));
  };

  const moveStep = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === procedureSteps.length - 1) return;

    const newSteps = [...procedureSteps];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[swapIndex]] = [newSteps[swapIndex], newSteps[index]];
    setProcedureSteps(newSteps.map((step, i) => ({ ...step, order: i + 1 })));
  };

  const saveSteps = async () => {
    try {
      await api.put(`/services/${editingService.id}`, {
        procedure_steps: procedureSteps.map((step, index) => ({ 
          description: step.description,
          order: index + 1
        }))
      });
      toast.success('Procedure steps saved successfully');
      setShowStepsModal(false);
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error('Failed to save procedure steps');
    }
  };

  const openMergeModal = () => {
    setShowMergeModal(true);
    setMergeStep(1);
    setMergePrimary(null);
    setMergeSecondary(null);
    setMergeChecklistFrom('primary');
    setMergeStepsFrom('primary');
  };

  const handleMerge = async () => {
    if (!mergePrimary || !mergeSecondary) return;
    setMerging(true);
    try {
      const res = await api.post('/services/merge', {
        primary_service_id: mergePrimary.id,
        secondary_service_id: mergeSecondary.id,
        keep_checklist_from: mergeChecklistFrom,
        keep_steps_from: mergeStepsFrom
      });
      toast.success(res.data.message);
      setShowMergeModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge services');
    } finally {
      setMerging(false);
    }
  };

  const handleUppercaseMigration = async () => {
    setMigratingUppercase(true);
    try {
      const res = await api.post('/services/uppercase-migration');
      toast.success(res.data.message);
      fetchServices();
    } catch (error) {
      toast.error('Migration failed');
    } finally {
      setMigratingUppercase(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Services Management</h1>
            <p className="text-sm text-gray-500 mt-1">Manage clinic services, checklists, and procedures</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleUppercaseMigration}
              variant="outline"
              size="sm"
              disabled={migratingUppercase}
              data-testid="uppercase-migration-btn"
            >
              {migratingUppercase ? 'Migrating...' : 'Convert All to CAPS'}
            </Button>
            <Button
              onClick={openMergeModal}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="merge-services-btn"
            >
              <Merge className="w-4 h-4 mr-2" />
              Merge Services
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="grid gap-4">
            {services.map(service => {
              const isExpanded = expandedService === service.id;
              const hasChecklist = service.pre_procedure_checklist?.length > 0;
              const hasSteps = service.procedure_steps?.length > 0;

              return (
                <div
                  key={service.id}
                  className={`bg-white border-2 rounded-xl transition overflow-hidden ${
                    service.status === 'inactive' ? 'border-gray-200 opacity-60' : 'border-purple-200'
                  }`}
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{service.name}</h3>
                          <Badge className={service.type === 'time_based' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}>
                            {service.type === 'time_based' ? 'Time-Based' : 'Queue-Based'}
                          </Badge>
                          <Badge className={service.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {service.status}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          {service.average_exposure_time_minutes > 0 && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-amber-600" />
                              <span>Avg: {Math.round(service.average_exposure_time_minutes)} min</span>
                            </div>
                          )}
                          {service.type === 'time_based' && (
                            <>
                              <div className="flex items-center gap-1">
                                <ListChecks className="w-4 h-4 text-purple-600" />
                                <span>{service.pre_procedure_checklist?.length || 0} checklist items</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <ClipboardList className="w-4 h-4 text-green-600" />
                                <span>{service.procedure_steps?.length || 0} procedure steps</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {service.type === 'time_based' && (
                          <>
                            <Button
                              onClick={() => openChecklistModal(service)}
                              variant="outline"
                              size="sm"
                              className="border-purple-300 text-purple-600 hover:bg-purple-50"
                            >
                              <ListChecks className="w-4 h-4 mr-1" />
                              Checklist
                            </Button>
                            <Button
                              onClick={() => openStepsModal(service)}
                              variant="outline"
                              size="sm"
                              className="border-green-300 text-green-600 hover:bg-green-50"
                            >
                              <ClipboardList className="w-4 h-4 mr-1" />
                              Steps
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => openEditModal(service)}
                          variant="outline"
                          size="sm"
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleToggleStatus(service)}
                          variant="outline"
                          size="sm"
                          className={service.status === 'active' ? 'border-amber-300 text-amber-600 hover:bg-amber-50' : 'border-green-300 text-green-600 hover:bg-green-50'}
                        >
                          {service.status === 'active' ? (
                            <><PowerOff className="w-4 h-4 mr-1" />Deactivate</>
                          ) : (
                            <><Power className="w-4 h-4 mr-1" />Activate</>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDelete(service)}
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                        {service.type === 'time_based' && (hasChecklist || hasSteps) && (
                          <Button
                            onClick={() => setExpandedService(isExpanded ? null : service.id)}
                            variant="ghost"
                            size="sm"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && service.type === 'time_based' && (
                    <div className="border-t border-gray-200 p-5 bg-gray-50">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Checklist */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <ListChecks className="w-4 h-4 text-purple-600" />
                            Pre-Procedure Checklist
                          </h4>
                          {hasChecklist ? (
                            <ul className="space-y-2">
                              {service.pre_procedure_checklist.map((item, index) => (
                                <li key={item.id || index} className="flex items-center gap-2 text-sm text-gray-600">
                                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium">
                                    {index + 1}
                                  </span>
                                  {item.description}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-400">No checklist items configured</p>
                          )}
                        </div>

                        {/* Procedure Steps */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-green-600" />
                            Procedure Steps
                          </h4>
                          {hasSteps ? (
                            <ul className="space-y-2">
                              {service.procedure_steps.sort((a, b) => a.order - b.order).map((step, index) => (
                                <li key={step.id || index} className="flex items-center gap-2 text-sm text-gray-600">
                                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium">
                                    {step.order}
                                  </span>
                                  {step.description}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-400">No procedure steps configured</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal || (!!editingService && !showChecklistModal && !showStepsModal)} onOpenChange={closeModals}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Create New Service'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Service Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter service name"
                  className="mt-2"
                />
              </div>

              {!editingService && (
                <div>
                  <Label>Service Type</Label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="time_based">Time-Based</option>
                    <option value="queue_based">Queue-Based</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button onClick={closeModals} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={editingService ? handleUpdate : handleCreate}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {editingService ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Checklist Modal */}
        <Dialog open={showChecklistModal} onOpenChange={() => setShowChecklistModal(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-purple-600" />
                Edit Checklist - {editingService?.name}
              </DialogTitle>
              <DialogDescription>
                Add items that therapists must check before starting the procedure
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Add new item */}
              <div className="flex gap-2">
                <Input
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="Enter checklist item..."
                  onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                />
                <Button onClick={addChecklistItem} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Checklist items */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {checklistItems.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No checklist items yet</p>
                ) : (
                  checklistItems.map((item, index) => (
                    <div 
                      key={item.id || index} 
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                    >
                      <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-sm">{item.description}</span>
                      <Button
                        onClick={() => removeChecklistItem(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChecklistModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveChecklist} className="bg-purple-600 hover:bg-purple-700">
                Save Checklist
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Procedure Steps Modal */}
        <Dialog open={showStepsModal} onOpenChange={() => setShowStepsModal(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-green-600" />
                Edit Procedure Steps - {editingService?.name}
              </DialogTitle>
              <DialogDescription>
                Define the procedure steps that therapists will follow during service
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Add new step */}
              <div className="flex gap-2">
                <Input
                  value={newStepDescription}
                  onChange={(e) => setNewStepDescription(e.target.value)}
                  placeholder="Enter procedure step..."
                  onKeyPress={(e) => e.key === 'Enter' && addProcedureStep()}
                />
                <Button onClick={addProcedureStep} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Procedure steps */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {procedureSteps.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No procedure steps yet</p>
                ) : (
                  procedureSteps.map((step, index) => (
                    <div 
                      key={step.id || index} 
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex flex-col gap-0.5">
                        <Button
                          onClick={() => moveStep(index, 'up')}
                          variant="ghost"
                          size="sm"
                          className="h-4 p-0"
                          disabled={index === 0}
                        >
                          <ChevronUp className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => moveStep(index, 'down')}
                          variant="ghost"
                          size="sm"
                          className="h-4 p-0"
                          disabled={index === procedureSteps.length - 1}
                        >
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </div>
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-sm">{step.description}</span>
                      <Button
                        onClick={() => removeProcedureStep(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStepsModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveSteps} className="bg-green-600 hover:bg-green-700">
                Save Steps
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Merge Services Modal */}
        <Dialog open={showMergeModal} onOpenChange={() => setShowMergeModal(false)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="merge-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Merge className="w-5 h-5 text-amber-600" />
                Merge Services {mergeStep === 1 ? '— Select Services' : mergeStep === 2 ? '— Choose What to Keep' : '— Confirm'}
              </DialogTitle>
            </DialogHeader>

            {/* Step 1: Select Primary & Secondary */}
            {mergeStep === 1 && (
              <div className="space-y-4 py-2">
                <p className="text-sm text-gray-500">Select the primary service (kept) and the service to merge into it (deleted after merge).</p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Primary Service (Keep)</Label>
                    <div className="space-y-1 max-h-60 overflow-y-auto border rounded-lg p-2">
                      {services.filter(s => s.id !== mergeSecondary?.id).map(s => (
                        <div
                          key={s.id}
                          onClick={() => setMergePrimary(s)}
                          className={`p-2 rounded cursor-pointer text-sm transition ${
                            mergePrimary?.id === s.id ? 'bg-green-100 border border-green-400 font-semibold' : 'hover:bg-gray-50'
                          }`}
                          data-testid={`merge-primary-${s.id}`}
                        >
                          {s.name}
                          <span className="text-xs text-gray-400 ml-1">({s.type === 'time_based' ? 'Time' : 'Queue'})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Merge Into Primary (Delete)</Label>
                    <div className="space-y-1 max-h-60 overflow-y-auto border rounded-lg p-2">
                      {services.filter(s => s.id !== mergePrimary?.id).map(s => (
                        <div
                          key={s.id}
                          onClick={() => setMergeSecondary(s)}
                          className={`p-2 rounded cursor-pointer text-sm transition ${
                            mergeSecondary?.id === s.id ? 'bg-red-100 border border-red-400 font-semibold' : 'hover:bg-gray-50'
                          }`}
                          data-testid={`merge-secondary-${s.id}`}
                        >
                          {s.name}
                          <span className="text-xs text-gray-400 ml-1">({s.type === 'time_based' ? 'Time' : 'Queue'})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {mergePrimary && mergeSecondary && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <span className="font-semibold text-green-700">{mergeSecondary.name}</span>
                    <ArrowRight className="w-4 h-4 inline mx-2 text-gray-500" />
                    <span className="font-semibold text-green-700">{mergePrimary.name}</span>
                    <p className="text-xs text-gray-500 mt-1">All appointments, job cards, and records from "{mergeSecondary.name}" will be moved to "{mergePrimary.name}". "{mergeSecondary.name}" will be deleted.</p>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowMergeModal(false)}>Cancel</Button>
                  <Button
                    disabled={!mergePrimary || !mergeSecondary}
                    onClick={() => setMergeStep(2)}
                    data-testid="merge-next-btn"
                  >
                    Next
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Step 2: Choose Checklist & Steps */}
            {mergeStep === 2 && (
              <div className="space-y-5 py-2">
                <p className="text-sm text-gray-500">Choose which service's procedure steps and pre-procedure checklist to keep.</p>

                {/* Pre-procedure Checklist Comparison */}
                <div>
                  <Label className="font-semibold text-sm mb-2 block">Pre-Procedure Checklist</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => setMergeChecklistFrom('primary')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        mergeChecklistFrom === 'primary' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="checklist-from-primary"
                    >
                      <p className="text-xs font-semibold text-gray-600 mb-1">{mergePrimary?.name}</p>
                      {mergePrimary?.pre_procedure_checklist?.length > 0 ? (
                        <ul className="space-y-1">
                          {mergePrimary.pre_procedure_checklist.map((item, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span className="text-purple-500 mt-0.5">&#8226;</span> {item.description}
                            </li>
                          ))}
                        </ul>
                      ) : <p className="text-xs text-gray-400">No checklist items</p>}
                    </div>
                    <div
                      onClick={() => setMergeChecklistFrom('secondary')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        mergeChecklistFrom === 'secondary' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="checklist-from-secondary"
                    >
                      <p className="text-xs font-semibold text-gray-600 mb-1">{mergeSecondary?.name}</p>
                      {mergeSecondary?.pre_procedure_checklist?.length > 0 ? (
                        <ul className="space-y-1">
                          {mergeSecondary.pre_procedure_checklist.map((item, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span className="text-purple-500 mt-0.5">&#8226;</span> {item.description}
                            </li>
                          ))}
                        </ul>
                      ) : <p className="text-xs text-gray-400">No checklist items</p>}
                    </div>
                  </div>
                </div>

                {/* Procedure Steps Comparison */}
                <div>
                  <Label className="font-semibold text-sm mb-2 block">Procedure Steps</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => setMergeStepsFrom('primary')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        mergeStepsFrom === 'primary' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="steps-from-primary"
                    >
                      <p className="text-xs font-semibold text-gray-600 mb-1">{mergePrimary?.name}</p>
                      {mergePrimary?.procedure_steps?.length > 0 ? (
                        <ol className="space-y-1">
                          {mergePrimary.procedure_steps.sort((a,b) => a.order - b.order).map((step, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span className="font-semibold text-green-600">{i + 1}.</span> {step.description}
                            </li>
                          ))}
                        </ol>
                      ) : <p className="text-xs text-gray-400">No procedure steps</p>}
                    </div>
                    <div
                      onClick={() => setMergeStepsFrom('secondary')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        mergeStepsFrom === 'secondary' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="steps-from-secondary"
                    >
                      <p className="text-xs font-semibold text-gray-600 mb-1">{mergeSecondary?.name}</p>
                      {mergeSecondary?.procedure_steps?.length > 0 ? (
                        <ol className="space-y-1">
                          {mergeSecondary.procedure_steps.sort((a,b) => a.order - b.order).map((step, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span className="font-semibold text-green-600">{i + 1}.</span> {step.description}
                            </li>
                          ))}
                        </ol>
                      ) : <p className="text-xs text-gray-400">No procedure steps</p>}
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setMergeStep(1)}>Back</Button>
                  <Button onClick={() => setMergeStep(3)} data-testid="merge-confirm-step-btn">
                    Review & Confirm
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Step 3: Confirm */}
            {mergeStep === 3 && (
              <div className="space-y-4 py-2">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-red-700 mb-2">This action cannot be undone!</p>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>&#8226; <strong>{mergeSecondary?.name}</strong> will be permanently deleted</li>
                    <li>&#8226; All its appointments, job cards, and records will be moved to <strong>{mergePrimary?.name}</strong></li>
                    <li>&#8226; Checklist from: <strong>{mergeChecklistFrom === 'primary' ? mergePrimary?.name : mergeSecondary?.name}</strong></li>
                    <li>&#8226; Procedure steps from: <strong>{mergeStepsFrom === 'primary' ? mergePrimary?.name : mergeSecondary?.name}</strong></li>
                  </ul>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setMergeStep(2)}>Back</Button>
                  <Button
                    onClick={handleMerge}
                    disabled={merging}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="merge-confirm-btn"
                  >
                    {merging ? 'Merging...' : 'Confirm Merge'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ServicesPage;
